import { useState } from 'react'
import { Box, Button, IconButton, TextField, Typography } from '@mui/material'
import { Plus, X } from 'lucide-react'

import { addLinkButton } from '../../../services/api/LinkButtonDataService'
import { LinkButton } from '../../../model/LinkButton'
import { onboardingStyles as s, ACCENT } from '../onboardingStyles'

const emptyRow = () => ({ title: '', url: '' })

const newId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`

export default function LinksStep({ pageId, goNext }) {
    const [rows, setRows] = useState([emptyRow()])
    const [saving, setSaving] = useState(false)

    const updateRow = (i, field, value) => {
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)))
    }

    const addRow = () => setRows((prev) => [...prev, emptyRow()])

    const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i))

    const handleContinue = async () => {
        if (!pageId) return
        setSaving(true)
        const toSave = rows.filter((r) => r.title.trim() && r.url.trim())
        // The backend assigns the real id + position; the draft uuid/position are ignored.
        await Promise.all(
            toSave.map((r, position) =>
                addLinkButton(new LinkButton(newId(), position, r.title.trim(), r.url.trim(), ''), pageId)
            )
        )
        goNext()
    }

    return (
        <Box>
            <Typography sx={s.title}>Add your links</Typography>
            <Typography sx={s.subtitle}>
                Your shop, your latest video, anything you want to share.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {rows.map((row, i) => (
                    <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Title"
                                placeholder="e.g. My Shop"
                                value={row.title}
                                onChange={(e) => updateRow(i, 'title', e.target.value)}
                            />
                            {rows.length > 1 && (
                                <IconButton aria-label="remove" onClick={() => removeRow(i)} size="small">
                                    <X size={18} />
                                </IconButton>
                            )}
                        </Box>
                        <TextField
                            fullWidth
                            size="small"
                            label="URL"
                            placeholder="https://…"
                            value={row.url}
                            onChange={(e) => updateRow(i, 'url', e.target.value)}
                        />
                    </Box>
                ))}
            </Box>

            <Button
                onClick={addRow}
                startIcon={<Plus size={18} />}
                sx={{
                    textTransform: 'none',
                    fontFamily: 'Catamaran',
                    fontWeight: 700,
                    color: ACCENT,
                    mt: 1.5,
                }}
            >
                Add another link
            </Button>

            <Box sx={s.footer}>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleContinue}
                    disabled={saving}
                    sx={s.ctaButton}
                >
                    {saving ? 'Saving…' : 'Continue'}
                </Button>
                <Button onClick={goNext} disabled={saving} sx={s.skipButton}>
                    Skip for now
                </Button>
            </Box>
        </Box>
    )
}
